import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DrawingTool } from '../types';
import DrawingCanvas from './DrawingCanvas';

interface VideoAsset {
  id: string;
  url?: string;
  file?: File;
}

interface VideoPlayerProps {
  asset: VideoAsset;
  onTimeUpdate: (time: number) => void;
  onPause: () => void;
  onPlay: () => void;
  seekTime: number | null;
  isDrawingEnabled: boolean;
  onDrawingChange: (dataUrl: string | null) => void;
  clearDrawingSignal: number;
  undoSignal: number;
  fps: number;
  isPlaying: boolean;
  annotationUrls: string[];
  drawingTool: DrawingTool;
  drawingColor: string;
  onDurationChange: (duration: number) => void;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  onToggleFullscreen?: () => void;
  loop?: boolean;
  isReversePlayback?: boolean;
  isBounceEnabled?: boolean;
  muted?: boolean;
  isScrubbing?: boolean;
}

const calculateVideoDimensions = (video: HTMLVideoElement, container: HTMLDivElement) => {
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;
  const videoAspect = video.videoWidth / video.videoHeight;
  const containerAspect = containerWidth / containerHeight;

  let videoDisplayWidth: number;
  let videoDisplayHeight: number;
  let videoX: number;
  let videoY: number;

  if (videoAspect > containerAspect) {
    videoDisplayWidth = containerWidth;
    videoDisplayHeight = containerWidth / videoAspect;
    videoX = 0;
    videoY = (containerHeight - videoDisplayHeight) / 2;
  } else {
    videoDisplayWidth = containerHeight * videoAspect;
    videoDisplayHeight = containerHeight;
    videoX = (containerWidth - videoDisplayWidth) / 2;
    videoY = 0;
  }

  return {
    width: videoDisplayWidth,
    height: videoDisplayHeight,
    x: videoX,
    y: videoY,
  };
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  asset,
  onTimeUpdate,
  onPause,
  onPlay,
  seekTime,
  isDrawingEnabled,
  onDrawingChange,
  clearDrawingSignal,
  undoSignal,
  fps: _fps,
  isPlaying,
  annotationUrls,
  drawingTool,
  drawingColor,
  onDurationChange,
  videoRef: externalVideoRef,
  loop = false,
  isReversePlayback = false,
  isBounceEnabled = false,
  muted = false,
  isScrubbing = false,
}) => {
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [videoPosition, setVideoPosition] = useState({ x: 0, y: 0 });
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const isScrubbingRef = useRef(isScrubbing);

  useEffect(() => {
    isScrubbingRef.current = isScrubbing;
  }, [isScrubbing]);

  const setVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      (internalVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = element;
      if (externalVideoRef) {
        (externalVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = element;
      }
    },
    [externalVideoRef]
  );

  useEffect(() => {
    if (asset.file) {
      const url = URL.createObjectURL(asset.file);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setBlobUrl(null);
  }, [asset.file]);

  const videoSrc = blobUrl || asset.url;

  useEffect(() => {
    const videoElement = internalVideoRef.current;
    return () => {
      if (videoElement) {
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
      }
    };
  }, [asset.id]);

  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video || !containerRef.current) return;

    const handleResize = () => {
      if (video && containerRef.current && video.videoWidth > 0 && video.videoHeight > 0) {
        const videoDims = calculateVideoDimensions(video, containerRef.current);
        setDimensions({ width: videoDims.width, height: videoDims.height });
        setVideoPosition({ x: videoDims.x, y: videoDims.y });
      } else if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({ width: clientWidth, height: clientHeight });
        setVideoPosition({ x: 0, y: 0 });
      }
    };

    video.addEventListener('loadedmetadata', handleResize);
    video.addEventListener('error', handleResize);
    window.addEventListener('resize', handleResize);
    if (video.videoWidth > 0) handleResize();

    return () => {
      video.removeEventListener('loadedmetadata', handleResize);
      video.removeEventListener('error', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [asset.id]);

  useEffect(() => {
    const video = internalVideoRef.current;
    if (video && seekTime !== null && Math.abs(video.currentTime - seekTime) > 0.001) {
      video.currentTime = seekTime;
    }
  }, [seekTime]);

  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video) return;
    video.loop = loop && !isBounceEnabled;
    video.muted = muted;
  }, [loop, isBounceEnabled, muted]);

  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video) return;

    if (isPlaying) {
      if (isReversePlayback && !isBounceEnabled) {
        // Reverse handled by PlayerView bounce logic via seeks; keep paused here if needed
      }
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Video playback failed:', error);
          }
        });
      }
    } else {
      video.pause();
    }
  }, [isPlaying, isReversePlayback, isBounceEnabled]);

  const handleTimeUpdate = () => {
    if (isScrubbingRef.current) return;
    if (internalVideoRef.current) {
      onTimeUpdate(internalVideoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (internalVideoRef.current) {
      onDurationChange(internalVideoRef.current.duration);
    }
  };

  const handleEnded = () => {
    if (loop && !isBounceEnabled) {
      const video = internalVideoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    } else {
      onPause();
    }
  };

  return (
    <div ref={containerRef} className="w-full h-full bg-black relative">
      <div
        className="absolute"
        style={{
          width: dimensions.width,
          height: dimensions.height,
          left: `${videoPosition.x}px`,
          top: `${videoPosition.y}px`,
        }}
      >
        <video
          ref={setVideoRef}
          key={asset.id}
          className="block w-full h-full object-contain"
          style={{ objectFit: 'contain', transform: 'translateZ(0)' }}
          src={videoSrc}
          crossOrigin="anonymous"
          muted={muted}
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={handleEnded}
          onClick={() => !isDrawingEnabled && (isPlaying ? onPause() : onPlay())}
        />

        {annotationUrls.map((url, index) => (
          <img
            key={`${index}-${url.slice(0, 32)}`}
            src={url}
            alt={`Comment Drawing ${index + 1}`}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10"
            style={{ objectFit: 'contain' }}
          />
        ))}

        <DrawingCanvas
          width={dimensions.width}
          height={dimensions.height}
          isDrawingEnabled={isDrawingEnabled}
          onDrawingChange={onDrawingChange}
          clearSignal={clearDrawingSignal}
          undoSignal={undoSignal}
          strokeColor={drawingColor}
          tool={drawingTool}
        />
      </div>
    </div>
  );
};

export default VideoPlayer;
